import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classes from "./affiliateSpotlight.module.css";
// @ts-ignore
import mergeClassNames from "merge-class-names";
import {
  AFFILIATE_ENABLED,
  AFFILIATE_IMPRESSION_DWELL_MS,
} from "../../constant/constant";
import {
  AffiliatePlacement,
  getProductById,
  getProductsForPlacement,
} from "../../data/affiliatePlacements";
import { AffiliateProduct } from "../../data/affiliateProducts";
import {
  dismissAffiliate,
  getRecentlyClickedIds,
  recordAffiliateClick,
  recordAffiliateImpression,
  shouldShowAffiliate,
} from "../../service/affiliateStorageService";
import {
  AFFILIATE_REL,
  trackAffiliateClick,
  trackAffiliateDismiss,
  trackAffiliateImpression,
} from "../../utils/trackAffiliate";
import AffiliateDisclosure from "../AffiliateDisclosure";

export interface AffiliateSpotlightProps {
  placement: AffiliatePlacement;
  className?: string;
  label?: string;
  count?: number;
  variant?: "card" | "compact";
  /** Ghim sản phẩm cụ thể thay vì để hệ thống chọn theo ngữ cảnh. */
  productIds?: string[];
  onDismissed?: () => void;
}

/**
 * Card gợi ý sản phẩm theo giọng của admin.
 *
 * Cố ý KHÔNG chặn thao tác: luôn có nút ẩn, ẩn đi thì người dùng không mất gì.
 * Đây là ranh giới tách nó khỏi kiểu ép click mà chính sách chống gian lận của
 * Shopee xử phạt.
 */
function AffiliateSpotlight(props: AffiliateSpotlightProps) {
  const {
    placement,
    className = "",
    label = "Admin dùng gì?",
    count = 1,
    variant = "card",
    productIds,
    onDismissed,
  } = props;

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const dwellTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [visible, setVisible] = useState(false);
  const [hidden, setHidden] = useState(false);

  // Quyết định hiển thị chốt một lần lúc mount để card không biến mất giữa chừng
  // khi bộ đếm impression thay đổi ở nơi khác.
  const [allowed] = useState(
    () => AFFILIATE_ENABLED && shouldShowAffiliate(placement)
  );

  const products = useMemo<AffiliateProduct[]>(() => {
    if (!allowed) return [];
    if (productIds && productIds.length > 0) {
      return productIds
        .map(getProductById)
        .filter(
          (product): product is AffiliateProduct =>
            !!product && product.active !== false
        )
        .slice(0, count);
    }
    return getProductsForPlacement(placement, count, getRecentlyClickedIds());
  }, [allowed, placement, count, productIds]);

  useEffect(() => {
    if (!allowed || products.length === 0) return;
    const anchor = anchorRef.current;
    if (!anchor) return;

    // Không có IntersectionObserver (trình duyệt rất cũ) thì hiện luôn.
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        setVisible(true);
        // Chỉ tính là đã xem khi card thực sự nằm trong tầm mắt đủ lâu.
        dwellTimerRef.current = setTimeout(() => {
          recordAffiliateImpression(placement);
          products.forEach((product) =>
            trackAffiliateImpression({
              productId: product.id,
              productName: product.name,
              category: product.category,
              placement,
            })
          );
        }, AFFILIATE_IMPRESSION_DWELL_MS);
      },
      { threshold: 0.5 }
    );

    observer.observe(anchor);
    return () => {
      observer.disconnect();
      if (dwellTimerRef.current) clearTimeout(dwellTimerRef.current);
    };
  }, [allowed, placement, products]);

  const handleDismiss = useCallback(() => {
    dismissAffiliate(placement);
    trackAffiliateDismiss(placement, products[0]?.id);
    setHidden(true);
    onDismissed?.();
  }, [placement, products, onDismissed]);

  const handleClick = useCallback(
    (product: AffiliateProduct) => {
      recordAffiliateClick(product.id);
      trackAffiliateClick({
        productId: product.id,
        productName: product.name,
        category: product.category,
        placement,
        url: product.url,
      });
    },
    [placement]
  );

  if (!allowed || hidden || products.length === 0) return null;

  return (
    <div
      ref={anchorRef}
      className={mergeClassNames(
        classes.root,
        variant === "compact" && classes.rootCompact,
        className
      )}
    >
      {visible && (
        <div className={classes.inner}>
          <div className={classes.header}>
            <span className={classes.label}>{label}</span>
            <div className={classes.headerRight}>
              <AffiliateDisclosure variant="chip" />
              <button
                type="button"
                className={classes.dismiss}
                onClick={handleDismiss}
                aria-label="Ẩn gợi ý này"
                title="Ẩn gợi ý này"
              >
                ×
              </button>
            </div>
          </div>

          {products.map((product) => (
            <ProductBody
              key={product.id}
              product={product}
              variant={variant}
              onClick={() => handleClick(product)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ProductBody(props: {
  product: AffiliateProduct;
  variant: "card" | "compact";
  onClick: () => void;
}) {
  const { product, variant, onClick } = props;
  const compact = variant === "compact";
  // Bản compact chỉ lấy 2 gạch đầu dòng đầu để không lấn màn hình kết quả.
  const notes = compact ? product.adminNote.slice(0, 2) : product.adminNote;

  return (
    <div className={classes.product}>
      <div className={classes.media}>
        {product.image && (
          <img
            src={product.image}
            alt={product.imageAlt ?? product.name}
            className={classes.image}
            width={compact ? 64 : 96}
            height={compact ? 64 : 96}
            loading="lazy"
          />
        )}
      </div>

      <div className={classes.content}>
        <div className={classes.byline}>
          <span className={classes.avatar} aria-hidden="true">
            A
          </span>
          <span className={classes.bylineName}>Admin Grade PTIT</span>
          {product.badge && (
            <span className={classes.badge}>{product.badge}</span>
          )}
        </div>

        <p className={classes.name}>{product.name}</p>
        {!compact && (
          <p className={classes.pitch}>{product.shortPitch}</p>
        )}

        {product.noteAttribution && (
          <p className={classes.attribution}>{product.noteAttribution}</p>
        )}

        <ul className={classes.notes}>
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>

        <div className={classes.footer}>
          {product.priceRange && (
            <span className={classes.price}>{product.priceRange}</span>
          )}
          <a
            href={product.url}
            className={classes.cta}
            target="_blank"
            rel={AFFILIATE_REL}
            onClick={onClick}
            onAuxClick={onClick}
          >
            Xem trên Shopee →
          </a>
        </div>
      </div>
    </div>
  );
}

export default AffiliateSpotlight;
